import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { NOT_READY_FOR_SALE_REASONS } from "../types";
import ExternalLinkAnchor from "../../common/ExternalLinkAnchor";
import { countryFormatter } from "../../../shared/utils";

export const style = {
  listContainer: { paddingTop: "8px", height: "77vh", width: "100%" },
  list: { "&:hover": { cursor: "pointer" } },
  cellEllipsis: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  actionButton: { opacity: 1 },
} as const;

const ellipsisCellRenderer = (params: GridRenderCellParams) => (
  <div style={style.cellEllipsis} title={params.value}>
    {params.value || "N/A"}
  </div>
);

const solutionTableColumns: Array<GridColDef> = [
  {
    field: "id",
    headerName: "ID",
    flex: 1,
    minWidth: 200,
    renderCell: ellipsisCellRenderer,
  },
  {
    field: "name",
    headerName: "Project Name",
    flex: 1,
    minWidth: 300,
    renderCell: ellipsisCellRenderer,
  },
  {
    field: "developer",
    headerName: "Developer",
    flex: 1,
    minWidth: 200,
    renderCell: (params) => (
      <div style={style.cellEllipsis} title={params.value}>
        {params.value || "-"}
      </div>
    ),
  },
  {
    field: "unitOfSale",
    headerName: "Unit",
    flex: 1,
    minWidth: 100,
    renderCell: (params) => (
      <div style={style.cellEllipsis} title={params.value}>
        {params.value || "-"}
      </div>
    ),
  },
  {
    field: "coBenefitEducation",
    headerName: "Co-benefits of Education",
    flex: 1,
    minWidth: 200,
    renderCell: (params) => (
      <div style={style.cellEllipsis} title={params.value}>
        {params.value || "N/A"}
      </div>
    ),
  },
  {
    field: "coBenefitEmployment",
    headerName: "Co-benefits of Employment",
    flex: 1,
    minWidth: 200,
    renderCell: (params) => (
      <div style={style.cellEllipsis} title={params.value}>
        {params.value || "N/A"}
      </div>
    ),
  },
  {
    field: "country",
    headerName: "Country",
    flex: 1,
    minWidth: 200,
    renderCell: (params) => {
      const countryCodes = params.row.country as string[];

      if (!countryCodes || countryCodes.length === 0) {
        return <div>-</div>;
      }

      const formattedCountries = countryCodes
        .map((code) => countryFormatter(code))
        .filter((country) => country !== undefined);

      if (formattedCountries.length === 0) {
        return <div>-</div>;
      }

      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          {formattedCountries.map((country, index) => (
            <span
              key={country!.name}
              style={{ display: "flex", alignItems: "center", gap: "4px" }}
            >
              <img
                src={country!.img}
                alt={`${country!.name} flag`}
                style={{ width: "20px" }}
              />
              <span>{country!.name}</span>
              {index < formattedCountries.length - 1 && <span>,</span>}
            </span>
          ))}
        </div>
      );
    },
  },
  {
    field: "isReadyForSale",
    headerName: "Ready For Sale",
    type: "boolean",
    flex: 1,
    minWidth: 100,
    renderCell: (params) =>
      params.value ? (
        <CheckIcon color="success" />
      ) : (
        <CloseIcon color="error" />
      ),
  },
  {
    field: "notReadyForSaleReason",
    headerName: "Not for Sale Reason",
    type: "singleSelect",
    valueOptions: [...NOT_READY_FOR_SALE_REASONS],
    flex: 1,
    minWidth: 200,
    renderCell: ellipsisCellRenderer,
  },
  {
    field: "isUsedForCarbonOffsetting",
    headerName: "Carbon Offsetting",
    flex: 1,
    minWidth: 200,
    renderCell: (params) =>
      params.value ? (
        <CheckIcon color="success" />
      ) : (
        <CloseIcon color="error" />
      ),
  },
  {
    field: "accountingMethodology",
    headerName: "Accounting Methodology",
    flex: 1,
    minWidth: 200,
    renderCell: (params) => (
      <div style={style.cellEllipsis} title={params.value}>
        {params.value || "N/A"}
      </div>
    ),
  },
  {
    field: "projectDescriptionDocument",
    headerName: "Project Description Document",
    flex: 1,
    minWidth: 200,
    renderCell: (params) =>
      params.value ? (
        <ExternalLinkAnchor
          url={`${params.value}`}
          dataTestid="projectDescriptionDocument-url-anchor"
        />
      ) : (
        "No Link"
      ),
  },
];

export default solutionTableColumns;
